"use client";

import { ArrowRight } from "lucide-react";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { trackEvent } from "@/lib/analytics";
import type { Locale } from "@/lib/i18n";

const EZYCHAT_URL = "https://ezychat.ai/";

/** GA4 `ezychat_click` → `location` param. One value per placement. */
export type EzyChatLocation =
  | "home_card"
  | "claude_plans_guide"
  | "claude_calculator"
  | "claude_pricing_blog"
  | "budget_2027";

/** Copy presets for the compact card (English keys, translated via t()). */
export type EzyChatCopy = "claude" | "budget";

const BODY_CLAUDE =
  "EzyChat puts your team on one shared WhatsApp inbox, with an optional AI sales agent that replies for you. Free and open-source, runs on your own computer.";

const COPY: Record<EzyChatCopy, { heading: string; body: string }> = {
  claude: { heading: "Using AI for customer chats too?", body: BODY_CLAUDE },
  budget: {
    heading: "Keeping costs down in 2027?",
    body: "EzyChat is a free, open-source WhatsApp team inbox with an optional AI sales agent, so your team can answer customers from one place.",
  },
};

/**
 * Card for EzyChat, our own free open-source WhatsApp team inbox (approved by
 * Jazz 2026-10-11). EzyChatButton is shared with the homepage hook card
 * (@/components/home/EzyChatHookCard); the default export is the compact
 * content-page card (Jazz, 2026-10-12). The compact card must sit AFTER the
 * page's own lead form / closing CTA, never between the hero and our form:
 * our leads come first. tests/ezychat-cta-placement.spec.ts enforces the order
 * in the built HTML. Its heading is a styled <p>, not an <h2>, so it does not
 * add a promo heading to the SEO owner pages' outline.
 *
 * Speed rules (see #431): no entrance animation and no opacity:0, so the card
 * is in the static HTML. The logo is lazy, small and has fixed dimensions, so it
 * can neither shift layout nor become the LCP element. No coloured side stripe
 * (Jazz rejected it); WhatsApp green only on the button.
 *
 * Claims must match ezychat.ai: free, open source (MIT), runs on your own
 * computer, AI sales agent is optional. The free version links ONE number per
 * install, so never promise multiple numbers. The logo is EzyChat's own asset
 * (ezychat.ai/brand/ezychat-logo.png), resized to WebP, not redrawn.
 */
export function EzyChatButton({
  locale,
  location,
}: {
  locale: Locale;
  location: EzyChatLocation;
}) {
  return (
    <>
      <a
        href={EZYCHAT_URL}
        target="_blank"
        rel="noopener"
        onClick={() => trackEvent("ezychat_click", { location })}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#20BD5A]"
      >
        {t(locale, "Try EzyChat free")}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </a>
      <span className="text-xs text-neutral-muted">
        {t(locale, "Built by Anchor Sprint")}
      </span>
    </>
  );
}

export default function EzyChatCard({
  copy,
  location,
  locale: localeProp,
  className = "",
}: {
  copy: EzyChatCopy;
  location: Exclude<EzyChatLocation, "home_card">;
  /** Optional: falls back to the LocaleProvider (e.g. inside blog MDX). */
  locale?: Locale;
  className?: string;
}) {
  const ctxLocale = useLocale();
  const locale = localeProp ?? ctxLocale;
  const { heading, body } = COPY[copy];
  const headingId = `ezychat-card-${location}`;

  return (
    <aside
      aria-labelledby={headingId}
      data-ezychat-card={location}
      className={`not-prose rounded-2xl border border-border bg-card p-4 sm:p-5 ${className}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <img
              src="/images/ezychat/ezychat-logo.webp"
              alt="EzyChat"
              width={28}
              height={27}
              loading="lazy"
              decoding="async"
              className="m-0 h-[27px] w-7 shrink-0 rounded-md"
            />
            <p
              id={headingId}
              className="m-0 font-display text-base font-bold leading-tight text-neutral-emphasis sm:text-lg"
            >
              {t(locale, heading)}
            </p>
          </div>
          <p className="m-0 mt-2 max-w-2xl text-sm leading-snug text-neutral-muted">
            {t(locale, body)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:shrink-0 sm:flex-col sm:items-end sm:gap-1.5">
          <EzyChatButton locale={locale} location={location} />
        </div>
      </div>
    </aside>
  );
}
